/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Flex, Grid, GridItem, useToast } from '@chakra-ui/react';
import { useEffect, Dispatch, SetStateAction, useState, useMemo } from 'react';

import {
  RaceTuningInput,
  RaceParticipants,
  Races,
  TunableMetrics,
} from 'api/generated/graphql';
import { RAlert } from 'components';
import { SetupSuggestions, Tyres } from 'types';
import { useTuning } from 'context';
import { useGetRaceById, useTuningForRace, useUserSettings } from 'hooks';
import StatSlider from 'components/StatSlider/StatSlider';

export interface EngineAndTuningProps {
  tuningSelections: RaceTuningInput;
  setTuningSelections: Dispatch<SetStateAction<RaceTuningInput>>;
  parts: RaceParticipants;
  setupSuggestions: SetupSuggestions;
  selectedTyres: Tyres;
  tuningError: unknown;
  carId: string;
  carStats?: Array<{
    metric: string;
    modifier: number;
    description: string;
    bars: number;
  }>;
}


const EngineAndTuning = ({
  tuningSelections,
  setTuningSelections,
  parts,
  setupSuggestions,
  selectedTyres,
  tuningError,
  carId,
  carStats,
}: EngineAndTuningProps) => {
  const {
    speed,
    setSpeed,
    setBraking,
    setFastTurns,
    setSlowTurns,
    setCooling,
    setHandling,
    setStability,
    setAcceleration,
  } = useTuning();

  const [sliderValues, setSliderValues] = useState<Record<string, number>>({});

  const { getUserSettings } = useUserSettings();

  const { data: raceByIdData, isLoading: isLoadingRaceById } = useGetRaceById({
    raceId: tuningSelections.raceId,
  });

  const metricsForCar = raceByIdData?.getRaceById?.metricsForCar;
  const userCarMetrics = metricsForCar?.find((metric) => metric.carId === carId);

  const tunableMetrics = raceByIdData?.getRaceById?.tunableMetrics;

  // Extrair campos únicos de tunableMetrics
  const availableMetrics = useMemo(() => {
    if (!tunableMetrics || tunableMetrics.length === 0) return [];
    
    // Usar um Set para armazenar campos únicos
    const uniqueFields = new Set<string>();
    
    // Coletar todos os campos únicos
    tunableMetrics.forEach((metric) => {
      if (metric.field) {
        uniqueFields.add(metric.field);
      }
    });
    
    // Converter para array e mapear para objetos com informações completas
    return Array.from(uniqueFields).map(field => {
      // Encontrar um exemplo deste campo para obter outras propriedades
      const exampleMetric = tunableMetrics.find(m => m.field === field);
      
      return {
        field: field,
        name: exampleMetric?.name || field,
        // Adicionar outras propriedades necessárias
      };
    });
  }, [tunableMetrics]);

  // console.log('availableMetrics', availableMetrics, 'tunableMetrics', tunableMetrics, 'userCarMetrics', userCarMetrics);


  useEffect(() => {
    setSpeed(userCarMetrics?.metrics?.speed || 5);
    setBraking(userCarMetrics?.metrics?.braking || 5);
    setFastTurns(userCarMetrics?.metrics?.fastTurns || 5);
    setSlowTurns(userCarMetrics?.metrics?.slowTurns || 5);
    setCooling(userCarMetrics?.metrics?.cooling || 5);
    setHandling(userCarMetrics?.metrics?.handling || 5);
    setStability(userCarMetrics?.metrics?.stability || 5);
    setAcceleration(userCarMetrics?.metrics?.acceleration || 5);
  }, [userCarMetrics]);

  
  const { tuningForRace } = useTuningForRace();
  const { mutateAsync: mutateTuning, isLoading: isLoadingTuningForRace } =
    tuningForRace;

  const toast = useToast();
  const handleSliderOnChangeEnd = async (tunedMetric: {
    name: string;
    val: number;
  }) => {
    try {
      setTuningSelections((selections) => {
        return {
          ...selections,
          [tunedMetric.name]: tunedMetric.val,
        };
      });

      await mutateTuning({
        raceTuningInput: {
          ...tuningSelections,
          [tunedMetric.name]: tunedMetric.val,
          tyresId: selectedTyres,
        },
      });

      switch (tunedMetric.name) {
        case 'speed': 
          setSpeed(tunedMetric.val);
          break;
        case 'fastTurns':
          setFastTurns(tunedMetric.val);
          break;
        case 'slowTurns':
          setSlowTurns(tunedMetric.val);
          break;
        case 'braking':
          setBraking(tunedMetric.val);
          break;
        case 'cooling':
          setCooling(tunedMetric.val);
          break;
        case 'handling':
          setHandling(tunedMetric.val);
          break;
        case 'stability':
          setStability(tunedMetric.val);
          break;
        case 'acceleration':
          setAcceleration(tunedMetric.val);
          break;
        default:
          break;
      }
    } catch (error) {
      toast({
        title: 'Error saving tuning values',
        description: 'There was an error saving tuning values.',
        status: 'error',
      });
      setTuningSelections((selections) => {
        return {
          ...selections,
          [tunedMetric.name]: sliderValues[tunedMetric.name],
        };
      });
      console.error('Error saving tuning values:', error);
    }
  };


  // Função para obter as métricas sugeridas para o pneu selecionado
  const getSuggestedTuningForTyre = useMemo(() => {
    if (!tunableMetrics || tunableMetrics.length === 0 || !selectedTyres) {
      return {};
    }

    // Filtrar as métricas para o pneu selecionado
    const metricsForSelectedTyre = tunableMetrics.filter(
      (metric) => metric.tyre === selectedTyres
    );

    // Criar um objeto com as sugestões para cada campo
    const suggestions: Record<string, { minGreen: number; maxGreen: number }> = {};
    
    metricsForSelectedTyre.forEach((metric) => {
      if (metric.field && metric.minGreen !== undefined && metric.maxGreen !== undefined) {
        suggestions[metric.field] = {
          minGreen: metric.minGreen,
          maxGreen: metric.maxGreen,
        };
      }
    });

    return suggestions;
  }, [tunableMetrics, selectedTyres]);

  // Função para obter o modificador específico para um campo
  const getMetricModifier = (field: string, carStats?: EngineAndTuningProps['carStats']): number => {
    if (!carStats) return 0;
    
    const stat = carStats.find(stat => stat.metric === field);
    return stat?.modifier || 0;
  };

  // Função para obter o valor mínimo sugerido para uma métrica
  const getMinSuggestion = (field: string, carStats?: EngineAndTuningProps['carStats']): number => {
    const suggestion = getSuggestedTuningForTyre[field]?.minGreen;
    const modifier = getMetricModifier(field, carStats);
    
    if (suggestion === undefined) {
      return 1; // Valor padrão se não houver sugestão
    }
    
    // Aplicar o modificador e garantir que o valor está dentro dos limites (1-10)
    return Math.max(0, Math.min(suggestion + modifier, 9));
  };

  // Função para obter o valor máximo sugerido para uma métrica
  const getMaxSuggestion = (field: string, carStats?: EngineAndTuningProps['carStats']): number => {
    const suggestion = getSuggestedTuningForTyre[field]?.maxGreen;
    const modifier = getMetricModifier(field, carStats);
    
    if (suggestion === undefined) {
      return 10; // Valor padrão se não houver sugestão
    }
    
    // Aplicar o modificador e garantir que o valor está dentro dos limites (1-10)
    return Math.max(1, Math.min(suggestion + modifier, 10));
  };

  useEffect(() => {
    const newSliderValues: Record<string, number> = {};
    let hasChanges = false;

    if (!availableMetrics || availableMetrics.length === 0) return;

    availableMetrics.forEach((metric) => {
      if (metric?.field) {
        // Usar o campo diretamente como chave
        const tuningKey = metric.field;
        
        // Obter o valor atual ou usar 5 como padrão
        const newValue = tuningSelections[tuningKey as keyof RaceTuningInput] !== undefined 
          ? (tuningSelections[tuningKey as keyof RaceTuningInput] as number) 
          : 5;
        
        // Comparar com o valor existente
        if (sliderValues[tuningKey] !== newValue) {
          newSliderValues[tuningKey] = newValue;
          hasChanges = true;
        } else {
          newSliderValues[tuningKey] = sliderValues[tuningKey] || newValue;
        }
      }
    });

    if (hasChanges) {
      setSliderValues(newSliderValues);
    }
  }, [tuningSelections, availableMetrics, sliderValues]);

  return (
    <Flex w="full" justifyContent="center">
      {Boolean(tuningError) && (
        <RAlert
          variant="error"
          description="There was an error saving tuning values."
          mt="8"
        />
      )}

      <Grid gap="1rem" gridTemplateColumns="repeat(1, 1fr)" w="full">
        {availableMetrics && 
          availableMetrics.map((metric, index) => {
            return (
              <GridItem key={index}>
                <StatSlider
                  statName={metric.name}
                  statField={metric.field}
                  value={metric && metric.field in sliderValues ? sliderValues[metric.field] : 1}
                  handleSliderOnChangeEnd={handleSliderOnChangeEnd}
                  tunableMetrics={(tunableMetrics || []) as unknown as TunableMetrics[]}
                  minSuggestion={getMinSuggestion(metric.field, carStats)}
                  maxSuggestion={getMaxSuggestion(metric.field, carStats)}
                />
              </GridItem>
            );
          })
        }
      </Grid>
    </Flex>
  );
};

export default EngineAndTuning;